import React from 'react'
import Input from '../../components/Input'
import Button from '../../components/Button'
import Select from 'react-select'
import { useForm, Controller } from 'react-hook-form'
import styled from 'styled-components'
import { connect } from 'react-redux'
import {
  create,
  createFile,
  update,
  updateFile,
  setLoading,
} from '../../actions'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const DynamicForm = ({
  form,
  create,
  update,
  createFile,
  updateFile,
  path,
  action,
  edit,
  fileRequired = false,
  notReturn = false,
  reload,
  afterSubmit,
  setLoading,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm()

  const navigate = useNavigate()

  const Fields = field => {
    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <Col span={field.col}>
            <Label>{field.label}</Label>
            <Input
              id={field.id}
              name={field.name}
              type={field.type}
              step="any"
              defaultValue={field.value}
              {...register(field.name, field.rules)}
            />
            <ErrorContainer>
              {errors[field.name] && errors[field.name].message}
            </ErrorContainer>
          </Col>
        )
      case 'hidden':
        return (
          <Input
            id={field.id}
            name={field.name}
            type={field.type}
            step="any"
            defaultValue={field.value}
            {...register(field.name, field.rules)}
          />
        )
      case 'select':
        return (
          <Col span={field.col}>
            <LabelSelect>{field.label}</LabelSelect>
            <Controller
              name={field.name}
              control={control}
              rules={field.rules}
              defaultValue={field.value}
              render={props => {
                const { field: controledFiel } = props
                const { value, onChange, ref } = controledFiel
                return (
                  <SelectContainer>
                    <Select
                      inputRef={ref}
                      options={field.options}
                      value={field.options.find(
                        c => c.value === value
                      )}
                      onChange={val => {
                        if (field.onChange) {
                          field.onChange(val.value)
                        }
                        onChange(val.value)
                      }}
                    />
                  </SelectContainer>
                )
              }}
            />

            <ErrorContainer>
              {errors[field.name] && errors[field.name].message}
            </ErrorContainer>
          </Col>
        )
      case 'file':
        return (
          <Col span={field.col}>
            <Label>{field.label}</Label>
            <Input
              id={field.id}
              name={field.name}
              type="file"
              {...register(field.name, field.rules)}
            />
            <ErrorContainer>
              {errors[field.name] && errors[field.name].message}
            </ErrorContainer>
          </Col>
        )
      case 'button':
        return (
          <Button
            id={field.id}
            name={field.name}
            label={field.label}
            type={field.type}
            {...register(field.name, field.rules)}
          />
        )
      case 'date':
        return (
          <Col span={field.col}>
            <Label>{field.label}</Label>
            <Controller
              name={field.name}
              control={control}
              rules={field.rules}
              defaultValue={field.value || null}
              render={({ field }) => (
                <StyledDatePicker
                  {...field}
                  selected={new Date(field.value)}
                  onChange={field.onChange}
                  dateFormat="yyyy-MM-dd"
                  locale="es-MX"
                  placeholderText={field.label}
                  autoComplete="off"
                />
              )}
            />
            <ErrorContainer>
              {errors[field.name] && errors[field.name].message}
            </ErrorContainer>
          </Col>
        )
      default:
        return null
    }
  }

  const onCancel = () => {
    navigate(-1)
  }

  const onSave = async data => {
    setLoading(true)
    const hasFile = form.some(field => field.type === 'file')
    const isReadyFileInInput = data?.file?.length > 0 ?? false

    if (fileRequired && !isReadyFileInInput) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Debes seleccionar un archivo',
      })
      return
    }

    try {
      if (edit) {
        hasFile
          ? await updateFile(path, action, data)
          : await update(path, action, data)
      } else {
        hasFile
          ? await createFile(path, action, data)
          : await create(path, action, data)
      }
      if (!notReturn) {
        onCancel()
      } else {
        reload()
      }

      if (afterSubmit) {
        afterSubmit()
      }
    } catch (error) {
      setLoading(false)
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Grid>
        {form.map(field => (
          <Fields key={field.id} {...field} />
        ))}
      </Grid>
      <ButtonConteinr>
        <Button type="submit" color={edit ? 'warning' : 'primary'}>
          {edit ? 'Actualizar' : 'Guardar'}
        </Button>
        <Button type="button" color="danger" onClick={onCancel}>
          Cancelar
        </Button>
      </ButtonConteinr>
    </form>
  )
}

const mapDispatchToProps = {
  create,
  update,
  createFile,
  updateFile,
  setLoading
}

export default connect(null, mapDispatchToProps)(DynamicForm)

const ButtonConteinr = styled.div`
  display: flex;
  margin-top: 10px;
`

const ErrorContainer = styled.div`
  color: red;
  font-size: 12px;
  margin: 5px;
`

const SelectContainer = styled.div`
  padding: 0.2rem 0 0 0;
  z-index: 1000;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-gap: 10px;
  margin-bottom: 10px;
  max-width: 100%;
  box-sizing: border-box;
  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
  }
`

const Col = styled.div`
  grid-column: span ${props => props.span};
  display: flex;
  flex-direction: column;
  position: relative;
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`

const StyledDatePicker = styled(DatePicker)`
  /* Reutilizamos los estilos de InputStyle */
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  outline-color: ${({ theme }) => theme.border};
  color: var(--dark);
  background-color: var(--white);
  background-clip: padding-box;
  border-radius: 3px;
  border: ${({ theme }) => theme.border};
  box-shadow: none;
  margin-top: 0.375rem;
  border-style: solid;
  border-radius: 7px;
  transition: border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  width: 100%;
  box-sizing: border-box;
  /* Ajuste para el calendario emergente */
  .react-datepicker__input-container {
    input {
      all: unset; /* Resetea los estilos internos */
      width: 100%;
    }
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colorButtonCircle};
  padding: 0.2rem 0;
  font-size: 0.75rem;
  position: absolute;
  top: -3px;
  left: 10px;
  z-index: 1;
  background-color: ${({ theme }) => theme.backgroundButton};
`

const LabelSelect = styled(Label)`
  z-index: 10000;
  top: -8px;
`
